import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { useEffect } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { v4 } from "uuid";
import { ContractBaseData } from "@/routes/processes/[processId]/rlt/offers/[offerId]/contract/_components/contract-base-data";
import { ContractComments } from "@/routes/processes/[processId]/rlt/offers/[offerId]/contract/_components/contract-comments";
import { Offer } from "@/services/backend/rlt/offers/types/offer";
import { ContractAddresses } from "@/routes/processes/[processId]/rlt/offers/[offerId]/contract/_components/contract-addresses";
import { useRltOfferShowQuery } from "@/services/backend/rlt/offers/api/offer-api";
import {
  useRltContractCreateForOfferMutation,
  useRltContractShowQuery,
} from "@/services/backend/rlt/contracts/api/contract-api";
import { TreatmentCycles } from "@/routes/processes/[processId]/rlt/offers/[offerId]/contract/_components/treatment-cycles";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function ContractForOffer({ offerId }: { offerId: string }) {
  return <NewContractGuard offerId={offerId} />;
}

function NewContractGuard({ offerId }: { offerId: string }) {
  const {
    data: offer,
    error,
    isLoading,
  } = useRltOfferShowQuery({ id: offerId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return null;
  }

  const contractId = offer?.contractId;

  if (contractId && contractId !== NullUUID) {
    return <ContractData contractId={contractId} offerId={offerId} />;
  }

  return <CreateContractButton offer={offer!} />;
}

function CreateContractButton({ offer }: { offer: Offer }) {
  const [create, { isLoading, error, reset }] =
    useRltContractCreateForOfferMutation();
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  const onClick = () => {
    if (isLoading) {
      return;
    }

    create({
      id: v4(),
      offerId: offer.id,
      companyId: offer.companyId,
      branchId: offer.branchId,
      customerId: offer.customerId,
      serviceObjectId: offer.serviceObjectId,
    });
  };

  return (
    <div>
      <Button onClick={onClick} disabled={isLoading || isImmutable(offer)}>
        {t("Neuen Vertrag erstellen")}
      </Button>
    </div>
  );
}

function ContractData({
  contractId,
  offerId,
}: {
  contractId: string;
  offerId: string;
}) {
  const {
    data: contract,
    isLoading,
    error,
  } = useRltContractShowQuery({
    id: contractId,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return null;
  }

  return (
    <div className="space-y-2">
      <ContractBaseData contract={contract!} />
      <TreatmentCycles contract={contract!} offerId={offerId} />
      <ContractAddresses contract={contract!} />
      <ContractComments contract={contract!} />
    </div>
  );
}
